import React, { useState } from 'react';
import {
  Plus, X, Trash2, RefreshCw, DollarSign,
  TrendingUp, TrendingDown, ChevronDown, Check,
} from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import EmptyState from './EmptyState';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Math.abs(n));

const FREQ_OPTIONS = [
  { id: 'weekly',    label: 'Weekly',    multiplier: 52 / 12 },
  { id: 'biweekly',  label: 'Biweekly',  multiplier: 26 / 12 },
  { id: 'monthly',   label: 'Monthly',   multiplier: 1 },
  { id: 'quarterly', label: 'Quarterly', multiplier: 1 / 3 },
  { id: 'yearly',    label: 'Yearly',    multiplier: 1 / 12 },
];

const CATEGORIES = [
  '🏠 Housing', '🚗 Transportation', '🍔 Food', '💪 Health', '📱 Subscriptions',
  '💡 Utilities', '🎓 Education', '💰 Savings', '💼 Income', '🎯 Other',
];

const EMPTY_FORM = {
  name: '', amount: '', type: 'expense', frequency: 'monthly',
  category: '', startDate: new Date().toISOString().slice(0, 10),
};

function monthlyAmount(item) {
  const freq = FREQ_OPTIONS.find(f => f.id === item.frequency) || FREQ_OPTIONS[2];
  return Math.abs(item.amount || 0) * freq.multiplier;
}

export default function RecurringTransactions() {
  const { userProfile, budgetData, updateSection } = useUserData();
  const recurring = userProfile?.recurring || [];

  // Bills from the Budget page, displayed read-only here
  const bills = budgetData?.bills || [];

  const [showAdd, setShowAdd]   = useState(false);
  const [form,    setForm]      = useState(EMPTY_FORM);
  const [synced,  setSynced]    = useState(false);

  // ── Derived totals ──────────────────────────────────────────────────────────
  const income        = recurring.filter(r => r.type === 'income').reduce((s, r) => s + monthlyAmount(r), 0);
  const recurExpenses = recurring.filter(r => r.type === 'expense').reduce((s, r) => s + monthlyAmount(r), 0);
  // Monthly bill cost: monthly bills × 1, quarterly ÷ 3, annual ÷ 12
  const billMonthly   = bills.reduce((s, b) => {
    const mult = b.frequency === 'quarterly' ? 1/3 : b.frequency === 'annual' ? 1/12 : 1;
    return s + b.amount * mult;
  }, 0);
  const expenses = recurExpenses + billMonthly;
  const net      = income - expenses;

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    if (!form.name.trim() || !form.amount) return;
    const item = {
      id:        crypto.randomUUID(),
      name:      form.name.trim(),
      amount:    parseFloat(form.amount),
      type:      form.type,
      frequency: form.frequency,
      category:  form.category,
      startDate: form.startDate,
      isActive:  true,
    };
    updateSection('user_profile', { ...userProfile, recurring: [...recurring, item] });
    setForm(EMPTY_FORM);
    setShowAdd(false);
  };

  const handleDelete = (id) => {
    updateSection('user_profile', { ...userProfile, recurring: recurring.filter(r => r.id !== id) });
  };

  const toggleActive = (id) => {
    updateSection('user_profile', {
      ...userProfile,
      recurring: recurring.map(r => r.id === id ? { ...r, isActive: !r.isActive } : r),
    });
  };

  // ── Sync to Budget ──────────────────────────────────────────────────────────
  const syncToBudget = () => {
    const activeRecurring = recurring.filter(r => r.isActive);

    // Sum up recurring income
    const recurringIncome = activeRecurring
      .filter(r => r.type === 'income')
      .reduce((s, r) => s + monthlyAmount(r), 0);

    // Build/update budget categories from recurring expenses
    const existingCats = budgetData?.categories || [];
    const updatedCats  = [...existingCats];

    activeRecurring.filter(r => r.type === 'expense').forEach(r => {
      const name = r.category ? `${r.category} · ${r.name}` : r.name;
      const existing = updatedCats.find(c => c.name === name || c.name === r.name);
      const monthlyAmt = Math.round(monthlyAmount(r));
      if (existing) {
        existing.budgeted = monthlyAmt;
      } else {
        updatedCats.push({
          name: r.name,
          icon: r.category?.split(' ')[0] || '🔄',
          budgeted: monthlyAmt,
          spent: 0,
        });
      }
    });

    const newBudget = {
      ...(budgetData || { categories: [] }),
      income: recurringIncome > 0 ? Math.round(recurringIncome) : (budgetData?.income || 0),
      categories: updatedCats,
    };

    updateSection('budget', newBudget);
    setSynced(true);
    setTimeout(() => setSynced(false), 2500);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Recurring Transactions</h1>
          <p className="text-slate-400 text-sm mt-0.5">Auto-populate your budget with regular income and bills</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Plus size={15} /> Add
        </button>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Monthly Income</div>
          <div className="text-xl font-bold text-emerald-400">+{fmt(income)}</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Monthly Expenses</div>
          <div className="text-xl font-bold text-rose-400">-{fmt(expenses)}</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Net Monthly</div>
          <div className={`text-xl font-bold ${net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {net >= 0 ? '+' : '-'}{fmt(net)}
          </div>
        </div>
      </div>

      {/* Sync button */}
      {recurring.length > 0 && (
        <div className="flex items-center gap-3 p-4 bg-blue-950/30 border border-blue-700/40 rounded-xl">
          <RefreshCw size={16} className="text-blue-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-200">Sync to Budget</p>
            <p className="text-xs text-slate-400">Automatically populate your Budget tab with these recurring amounts.</p>
          </div>
          <button
            onClick={syncToBudget}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              synced
                ? 'bg-emerald-600 text-white'
                : 'bg-blue-600 hover:bg-blue-500 text-white'
            }`}
          >
            {synced ? <><Check size={14} /> Synced!</> : <><RefreshCw size={14} /> Sync Now</>}
          </button>
        </div>
      )}

      {/* List */}
      {recurring.length === 0 && bills.length === 0 ? (
        <EmptyState
          icon="🔄"
          title="No recurring transactions yet"
          description="Add your salary, rent, subscriptions, and other regular income and expenses."
          actionLabel="Add First Transaction"
          onAction={() => setShowAdd(true)}
        />
      ) : (
        <div className="space-y-3">
          {/* Income group */}
          {recurring.filter(r => r.type === 'income').length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={14} className="text-emerald-400" />
                <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Income</h3>
              </div>
              <div className="space-y-2">
                {recurring.filter(r => r.type === 'income').map(r => (
                  <RecurringRow key={r.id} item={r} onDelete={handleDelete} onToggle={toggleActive} />
                ))}
              </div>
            </div>
          )}

          {/* Expense group */}
          {recurring.filter(r => r.type === 'expense').length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2 mt-4">
                <TrendingDown size={14} className="text-rose-400" />
                <h3 className="text-xs font-semibold text-rose-400 uppercase tracking-wider">Expenses</h3>
              </div>
              <div className="space-y-2">
                {recurring.filter(r => r.type === 'expense').map(r => (
                  <RecurringRow key={r.id} item={r} onDelete={handleDelete} onToggle={toggleActive} />
                ))}
              </div>
            </div>
          )}

          {/* Bills (from Budget page — read-only here) */}
          {bills.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2 mt-4">
                <TrendingDown size={14} className="text-amber-400" />
                <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Bills</h3>
                <span className="text-xs text-slate-500 font-normal normal-case">· managed in Budget → Bills</span>
              </div>
              <div className="space-y-2">
                {bills.map(bill => {
                  const mult    = bill.frequency === 'quarterly' ? 1/3 : bill.frequency === 'annual' ? 1/12 : 1;
                  const monthly = bill.amount * mult;
                  return (
                    <div key={bill.id}
                      className="flex items-center gap-4 p-4 rounded-xl border bg-slate-800 border-slate-700">
                      {/* Icon */}
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-900/40">
                        <TrendingDown size={15} className="text-amber-400" />
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-200 truncate">{bill.name}</div>
                        <div className="text-xs text-slate-500">
                          {bill.category && `${bill.category} · `}
                          <span className="capitalize">{bill.frequency}</span>
                          {' · '}due day {bill.dueDay}
                        </div>
                      </div>
                      {/* Amounts */}
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-semibold text-rose-400">
                          -{fmt(bill.amount)}
                        </div>
                        {bill.frequency !== 'monthly' && (
                          <div className="text-xs text-slate-500">{fmt(monthly)}/mo equiv.</div>
                        )}
                      </div>
                      {/* Read-only badge */}
                      <span className="text-xs text-amber-600 bg-amber-900/20 border border-amber-800/30
                                       px-2 py-0.5 rounded-full flex-shrink-0">
                        Budget
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={e => e.target === e.currentTarget && setShowAdd(false)}
        >
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h2 className="text-lg font-bold text-slate-100">Add Recurring Transaction</h2>
              <button onClick={() => setShowAdd(false)} className="text-slate-500 hover:text-slate-300 p-1 rounded-lg hover:bg-slate-800">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 pb-6 space-y-4">
              {/* Type toggle */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => set('type', 'income')}
                  className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                    form.type === 'income'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-emerald-700'
                  }`}
                >
                  💰 Income
                </button>
                <button
                  onClick={() => set('type', 'expense')}
                  className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                    form.type === 'expense'
                      ? 'bg-rose-600 text-white border-rose-600'
                      : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-rose-700'
                  }`}
                >
                  💳 Expense
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Name</label>
                <input
                  type="text"
                  placeholder={form.type === 'income' ? 'e.g. Salary, Freelance' : 'e.g. Rent, Netflix'}
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Amount ($)</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={e => set('amount', e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Frequency</label>
                  <div className="relative">
                    <select
                      value={form.frequency}
                      onChange={e => set('frequency', e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500 appearance-none"
                    >
                      {FREQ_OPTIONS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                    <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Category (optional)</label>
                <div className="relative">
                  <select
                    value={form.category}
                    onChange={e => set('category', e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500 appearance-none"
                  >
                    <option value="">— Select category —</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Start Date</label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={e => set('startDate', e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Preview monthly amount */}
              {form.amount && (
                <div className="p-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-xs text-slate-400">
                  Monthly equivalent:{' '}
                  <span className={`font-semibold ${form.type === 'income' ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {form.type === 'income' ? '+' : '-'}
                    {fmt(parseFloat(form.amount || 0) * (FREQ_OPTIONS.find(f => f.id === form.frequency)?.multiplier || 1))}
                    /mo
                  </span>
                </div>
              )}

              <button
                onClick={handleAdd}
                disabled={!form.name.trim() || !form.amount}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold py-2.5 rounded-xl transition-all text-sm"
              >
                Add Transaction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecurringRow({ item, onDelete, onToggle }) {
  const freq    = FREQ_OPTIONS.find(f => f.id === item.frequency) || FREQ_OPTIONS[2];
  const monthly = monthlyAmount(item);
  const isIncome = item.type === 'income';

  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border transition-all group ${
      item.isActive
        ? 'bg-slate-800 border-slate-700'
        : 'bg-slate-800/40 border-slate-700/40 opacity-50'
    }`}>
      {/* Toggle */}
      <button
        onClick={() => onToggle(item.id)}
        className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
          item.isActive
            ? 'bg-blue-600 border-blue-600'
            : 'border-slate-600'
        }`}
      >
        {item.isActive && <Check size={10} className="text-white" />}
      </button>

      {/* Icon */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isIncome ? 'bg-emerald-900/40' : 'bg-rose-900/40'
      }`}>
        {isIncome
          ? <DollarSign size={15} className="text-emerald-400" />
          : <TrendingDown size={15} className="text-rose-400" />
        }
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-200 truncate">{item.name}</div>
        <div className="text-xs text-slate-500">
          {freq.label} {item.category ? `· ${item.category}` : ''}
        </div>
      </div>

      {/* Amounts */}
      <div className="text-right flex-shrink-0">
        <div className={`text-sm font-bold ${isIncome ? 'text-emerald-400' : 'text-slate-300'}`}>
          {isIncome ? '+' : '-'}{fmt(item.amount)}/{
            { weekly: 'wk', biweekly: '2wk', monthly: 'mo', quarterly: 'qtr', yearly: 'yr' }[item.frequency] || 'mo'
          }
        </div>
        <div className="text-xs text-slate-500">{fmt(monthly)}/mo equiv.</div>
      </div>

      {/* Delete */}
      <button
        onClick={() => onDelete(item.id)}
        className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-600 hover:text-rose-400 transition-all rounded-lg hover:bg-rose-950/30"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}
